import { CompanyCountryExposureItem } from '@api/api/theme-overview';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { config } from '@terminal/config/app.config';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { SectionBase } from '@terminal/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import type { SharedTableColumn } from '@terminal/modules/overview-knowledge-graph/components/shared/shared-table/SharedTable';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useCompanyCountryExposure } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { useGeographicDrawer } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/useGeographicDrawer';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import styles from './GeographicExposureSection.module.scss';

export interface GeographicExposureSectionProps {
  entityTag: string;
  source?: PosthogTrackingSource;
  isInDrawer?: boolean;
}

export const GeographicExposureSection = ({
  entityTag,
  source,
  isInDrawer = false,
}: GeographicExposureSectionProps) => {
  const { t } = useTranslation(['widget', 'countries']);
  const [searchParams] = useSearchParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { openCountry } = useGeographicDrawer({ entityTag, source });

  const {
    data = [],
    isSuccess,
    isError,
    isFetching,
    error,
  } = useCompanyCountryExposure({
    entityTag,
  });
  const isCountryExposureNotFound = error?.status === httpCode.notFound;

  const navigateToKnowledgeGraph = () => {
    navigateToPage({
      entityTag,
      source: source ?? POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      tab: TabsType.Connections,
      searchParams: getKnowledgeGraphSearchParams({
        entityTag,
        searchParams,
        subtab: KnowledgeGraphTabType.GeographicExposure,
      }),
    });
  };

  const tableColumns: SharedTableColumn<CompanyCountryExposureItem>[] = [
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.country'),
      key: 'code',
      render: value => (
        <div className={styles.root}>
          <FlagIcon countryCode={value} customSize={28} variant="circular" />
          <div className={styles.title}>{t(`countries:${value}`)}</div>
        </div>
      ),
    },
  ];

  return (
    <SectionBase
      title={t('widget:assetOverview.knowledgeGraphRevamp.geographicExposure')}
      isError={isError && !isCountryExposureNotFound}
      isEmpty={(isSuccess && data.length === 0) || isCountryExposureNotFound}
      emptyTitle={t(
        'widget:assetOverview.knowledgeGraphRevamp.empty.geographicExposure'
      )}
      tableData={data.slice(0, 3).map(d => ({ ...d, key: d.code }))}
      tableColumns={tableColumns}
      isFetching={isFetching}
      onExpand={navigateToKnowledgeGraph}
      onRowClick={item => {
        if (config.app.isTeamsApp) {
          return;
        }
        isInDrawer ? navigateToKnowledgeGraph() : openCountry(item.code);
      }}
      hideExpandButton={config.app.isTeamsApp}
    />
  );
};
